import { Fragment, ReactNode } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { cn } from '@lib/utils/cn';

export interface DropdownMenuItem {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  variant?: 'default' | 'danger' | 'success' | 'warning';
  disabled?: boolean;
  divider?: boolean;
  'data-testid'?: string;
}

interface DropdownMenuProps {
  trigger: ReactNode;
  items: DropdownMenuItem[];
  align?: 'left' | 'right';
  className?: string;
  'data-testid'?: string;
}

const variantClasses = {
  default: 'text-secondary-700 dark:text-secondary-200 hover:bg-secondary-100 dark:hover:bg-secondary-700',
  danger: 'text-danger-700 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20',
  success: 'text-success-700 dark:text-success-400 hover:bg-success-50 dark:hover:bg-success-900/20',
  warning: 'text-warning-700 dark:text-warning-400 hover:bg-warning-50 dark:hover:bg-warning-900/20',
};

export function DropdownMenu({
  trigger,
  items,
  align = 'right',
  className,
  'data-testid': testId,
}: DropdownMenuProps) {
  return (
    <Menu as="div" data-testid={testId} className={cn('relative inline-block text-left', className)}>
      <Menu.Button as="div" className="cursor-pointer">
        {trigger}
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        {/* Panel con el radio, el borde fino y la sombra larga del sistema
            (Claude Design), en vez del rounded-md + ring por defecto. */}
        <Menu.Items
          data-testid={testId ? `${testId}-items` : undefined}
          className={cn(
            'absolute z-50 mt-2 w-60 origin-top-right overflow-hidden rounded-xl',
            'border border-[#ececec] bg-white shadow-[0_1px_2px_rgba(10,34,51,.03),0_18px_40px_-16px_rgba(10,34,51,.22)]',
            'dark:border-secondary-600 dark:bg-secondary-800',
            'focus:outline-none',
            align === 'left' ? 'left-0' : 'right-0'
          )}
        >
          <div className="py-1.5">
            {items.map((item, index) => (
              <Fragment key={index}>
                {item.divider ? (
                  <div className="my-1.5 border-t border-[#f0f0f0] dark:border-secondary-700" />
                ) : (
                  <Menu.Item disabled={item.disabled}>
                    {({ active }) => (
                      <button
                        onClick={item.onClick}
                        disabled={item.disabled}
                        data-testid={item['data-testid'] ?? (testId ? `${testId}-item-${index}` : undefined)}
                        className={cn(
                          'group flex w-full items-center px-4 py-2.5 text-[14px] font-medium transition-colors',
                          item.disabled
                            ? 'cursor-not-allowed opacity-50'
                            : active
                            ? variantClasses[item.variant || 'default']
                            : 'text-secondary-700 dark:text-secondary-200'
                        )}
                      >
                        {item.icon && (
                          <item.icon
                            className={cn(
                              'mr-3 h-4 w-4 shrink-0',
                              item.disabled ? 'opacity-50' : ''
                            )}
                            aria-hidden="true"
                          />
                        )}
                        <span className="text-left">{item.label}</span>
                      </button>
                    )}
                  </Menu.Item>
                )}
              </Fragment>
            ))}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
